import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router-dom';

export type ButtonVariant = 'primary' | 'ghost' | 'danger' | 'amber';
export type ButtonSize = 'sm' | 'md';

const BASE =
  'inline-flex items-center justify-center gap-2 font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900';

const VARIANT: Record<ButtonVariant, string> = {
  primary:
    'bg-blue-600 text-white shadow-[0_6px_16px_-6px_rgba(37,99,235,0.6)] hover:bg-blue-700 hover:shadow-[0_8px_20px_-6px_rgba(37,99,235,0.7)] focus-visible:ring-blue-500',
  ghost:
    'border border-slate-200 bg-white/80 text-slate-700 hover:bg-slate-50 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10 focus-visible:ring-slate-400',
  danger:
    'border border-red-200 text-red-600 hover:bg-red-50 dark:border-red-500/20 dark:text-red-400 dark:hover:bg-red-500/10 focus-visible:ring-red-500',
  amber:
    'bg-gradient-to-r from-amber-400 to-amber-500 text-[#1A1205] shadow-[0_6px_16px_-6px_rgba(245,158,11,0.5)] hover:from-amber-500 hover:to-amber-600 focus-visible:ring-amber-500',
};

const SIZE: Record<ButtonSize, string> = {
  sm: 'rounded-[9px] px-3 py-1.5 text-[12.5px]',
  md: 'rounded-[11px] px-4 py-2.5 text-sm',
};

interface CommonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children: ReactNode;
  className?: string;
}

interface ButtonProps extends CommonProps, Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'> {}

/**
 * Project-wide button. The four variants are exhaustive — pick by intent:
 *  - `primary` (default): the main action on the surface (Start session, Save).
 *  - `ghost`: a secondary, lower-emphasis action (Cancel, See all).
 *  - `danger`: destructive or revealing-a-warning actions (Reset progress).
 *  - `amber`: the marketing/upgrade highlight (Upgrade, Go Premium).
 * Use `LinkButton` instead of `Button` whenever the action is navigation
 * (`to=...`) — it renders a react-router `<Link>` with the same look.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', className = '', children, ...rest },
  ref,
) {
  return (
    <button ref={ref} className={`${BASE} ${VARIANT[variant]} ${SIZE[size]} ${className}`} {...rest}>
      {children}
    </button>
  );
});

interface LinkButtonProps extends CommonProps, Omit<LinkProps, 'className' | 'children'> {}

/**
 * `Link`-flavored button — same look as `Button`, but routes via react-router.
 */
export function LinkButton({ variant = 'primary', size = 'md', className = '', children, ...rest }: LinkButtonProps) {
  return (
    <Link className={`${BASE} ${VARIANT[variant]} ${SIZE[size]} ${className}`} {...rest}>
      {children}
    </Link>
  );
}
